import { Api, apiFactory } from '../api'
import { Cron } from '../cron'
import { Database, databaseFactory } from '../database'
import { Metrics } from '../metrics'
import { EventLogType } from '../types'
import { ClientLocation, clientDetection, getClientLocation, getDeviceId, isBrowser } from '../utils'
import { EventArgs, InitArgs, Logger, SetEventLogArgs, SetItemArgs} from './event-logs-main.types'

const INIT_ITEMS = 0
const MAX_ITEMS = 30

export abstract class EventLogMain {
  protected enableEvents
  protected enableLogs
  protected api: Api
  protected database: Database
  protected organizationName: string
  protected browserInfo: UAParser.IResult = clientDetection()
  protected cron: Cron | undefined = undefined
  protected publicApiKey: string
  protected isBrowser = isBrowser()
  #location: ClientLocation | undefined
  #platform: string
  public language: string | undefined
  public country: string | undefined
  #xVersion: string | undefined
  protected enableCron: boolean | undefined
  #totalItems = INIT_ITEMS
  protected maxBatchQueued = MAX_ITEMS
  #workflow: string
  public deviceId: string | undefined
  protected batchTime: number | undefined
  protected clientAppDomain: string | undefined
  protected metrics: Metrics
  protected debug: boolean = false

  constructor({
    organizationName,
    platform = 'Web',
    batchTime,
    enableEvents = true,
    enableLogs = true,
    enableCron,
    debug = false,
    publicApiKey,
    language,
    xVersion,
    maxBatchQueued = MAX_ITEMS,
    workflow = 'SDK_CHECKOUT',
    country,
    cookieName = 'sdk_payments',
    deviceId = getDeviceId(cookieName),
    clientAppDomain,
    enableMetrics = false,
    apiUrl,
  }: InitArgs) {
    this.debug = debug
    this.enableEvents = enableEvents
    this.enableLogs = enableLogs
    this.#platform = platform
    this.language = language
    this.country = country
    this.#xVersion = xVersion
    this.enableCron = enableCron
    this.api = apiFactory(platform, publicApiKey, language, debug, apiUrl)
    this.database = databaseFactory(organizationName)
    this.#totalItems = this.database.count()
    this.organizationName = organizationName
    this.publicApiKey = publicApiKey
    this.maxBatchQueued = maxBatchQueued
    this.#workflow = workflow
    this.deviceId = deviceId
    this.batchTime = batchTime
    this.clientAppDomain = clientAppDomain
    this.#getLocation()

    if (enableCron) {
      this.startCron()
    }


    this.metrics = new Metrics({
      debug,
      organizationName,
      api: this.api,
      enableMetrics: enableMetrics,
      enableCron: Boolean(enableCron),
      batchTime,
    })

    if(enableCron) {
      this.metrics.startCron()
    }
  }

  abstract event(eventArgs: EventArgs): Promise<void> | undefined

  abstract logger: Logger

  abstract sendBatch(): Promise<void>
  abstract sendMetricBatch(): Promise<void>

  protected async startCron() {
    if (this.cron) {
      this.cron.stop()
    }

    if (this.enableCron && this.batchTime && (this.enableEvents || this.enableLogs)) {
      this.cron = new Cron(this.batchTime, this.api.sendBatch.bind(this.api), this.database)
      this.cron.sendBatch()
    }
  }

  protected setEvent({
    eventLogArgs,
    level,
  }: SetEventLogArgs) {
    if (!this.enableEvents) {
      return
    }

    const eventArgs = {
      ...eventLogArgs,
      location_lat: this.#location?.latitude,
      location_lng: this.#location?.longitude,
    }
    
    return this.setItem({
      eventLogArgs: eventArgs,
      level,
      type: EventLogType.EVENT,
    })
  }

  protected setLog({
    eventLogArgs,
    level,
  }: SetEventLogArgs) {
    if (!this.enableLogs) {
      return
    }

    return this.setItem({
      eventLogArgs,
      level,
      type: EventLogType.LOG,
    })
  }

  protected async setItem({
    eventLogArgs,
    level,
    type,
  }: SetItemArgs) {
    if (this.debug) {
      console.log('Loggers',{
        type,
        level,
        eventLogArgs,
      })
    }
    await this.database.setItem(
      {
        os: this.browserInfo?.os?.name,
        organization_name: this.organizationName,
        client_app_domain: this.clientAppDomain ?? this.getDomain(),
        ...eventLogArgs,
        device_id: this.deviceId,
        metadata: {
          ...eventLogArgs.metadata,
          browser_info: this.browserInfo,
          public_api_key: this.publicApiKey,
          language: this.language,
          country: this.country,
        },
        headers: {
          user_agent: this.browserInfo?.ua,
          platform: this.#platform,
          accept_language: this.language,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          'x-version': this.#xVersion,
          workflow: this.#workflow,
          ...eventLogArgs.headers,
        },
        url: eventLogArgs.url ?? this.getUrl(),
        level,
        type,
      }
    )

    this.#totalItems += 1

    if (this.#totalItems >= this.maxBatchQueued) {
      this.#totalItems = INIT_ITEMS
      await this.sendBatch()
    }
  }

  protected getUrl() {
    if (this.isBrowser) {
      return window.location.href
    }
  
    return null
  }

  protected getDomain() {
    if (this.isBrowser) {
      return window.location.host
    }
  
    return null
  }

  async #getLocation() {
    if (this.isBrowser) {
      this.#location = await getClientLocation()
    }
  }
}
